import { signUp } from "aws-amplify/auth";
import { useEffect, useRef, useState } from "react";
import PasswordRequirements from "../../utils/passwordRequirements";
import { isPasswordValid } from "../../utils/passwordValidation";
import TestEnvironmentNotice from "./TestEnvironmentNotice";

export default function SignUp({ onSuccess, onBackToLogin }) {
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedAgb, setAcceptedAgb] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showTestNotice, setShowTestNotice] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const registrationPending = useRef(false);
  const passwordRef = useRef(null);
  const passwordValid = isPasswordValid(password);

  useEffect(() => {
    const container = document.querySelector(
      ".greenone-sign-up"
    );

    if (!container) {
      return undefined;
    }

    function styleSdxButton(button) {
      const shadowRoot = button.shadowRoot;

      if (
        !shadowRoot ||
        shadowRoot.querySelector(
          "[data-greenone-signup-button-styles]"
        )
      ) {
        return;
      }

      const style = document.createElement("style");

      style.setAttribute(
        "data-greenone-signup-button-styles",
        "true"
      );

      style.textContent = `
        button,
        button.primary {
          background: #005654 !important;
          border-color: #005654 !important;
          color: #fff !important;
        }

        button:hover,
        button.primary:hover {
          background: #026F6D !important;
          border-color: #026F6D !important;
          color: #fff !important;
        }

        button:focus-visible,
        button.primary:focus-visible {
          outline: 3px solid #005654 !important;
          outline-offset: 2px !important;
        }
        :host([theme="secondary"]) button {
          background: #fff !important;
          color: #005654 !important;
        }

        :host([theme="secondary"]) button:hover {
          background: #eef5f4 !important;
          color: #005654 !important;
        }
      `;

      shadowRoot.appendChild(style);
    }

    function styleSdxInput(input) {
      const shadowRoot = input.shadowRoot;

      if (
        !shadowRoot ||
        shadowRoot.querySelector(
          "[data-greenone-signup-input-styles]"
        )
      ) {
        return;
      }

      const style = document.createElement("style");

      style.setAttribute(
        "data-greenone-signup-input-styles",
        "true"
      );

      style.textContent = `
        input:focus,
        input:focus-visible {
          border-color: #026F6D !important;
          outline-color: #026F6D !important;
          box-shadow: 0 0 0 1px #026F6D !important;
        }

        :host(:focus-within) input {
          border-color: #026F6D !important;
          outline-color: #026F6D !important;
          box-shadow: 0 0 0 1px #026F6D !important;
        }
          input::-ms-reveal,
            input::-ms-clear {
            display: none !important;
  }
      `;

      shadowRoot.appendChild(style);
    }

    function applyComponentStyles() {
      container
        .querySelectorAll("sdx-button")
        .forEach(styleSdxButton);

      container
        .querySelectorAll("sdx-input")
        .forEach(styleSdxInput);
    }

    applyComponentStyles();

    const observer = new MutationObserver(
      applyComponentStyles
    );

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (passwordRef.current) {
      passwordRef.current.type = showPassword ? "text" : "password";
    }
  }, [showPassword]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registrationPending.current) return;
    setError(null);
    setMessage(null);

    if (!passwordValid) {
      setError("Das Passwort erfüllt nicht alle Anforderungen.");
      return;
    }

    if (!acceptedAgb) {
      setError("Sie müssen die AGB akzeptieren, um sich zu registrieren.");
      return;
    }

    setShowTestNotice(true);
  };

  const handleAcceptTestNotice = async () => {
    if (registrationPending.current) return;
    registrationPending.current = true;
    setIsRegistering(true);

    try {
      await signUp({
        username: email, // E-Mail als Login-Identifier
        password,
        options: {
          userAttributes: {
            email,
            "custom:company": company, // Custom Attribute in Cognito
          },
        },
      });
      setMessage(
        "Registrierung erfolgreich! Bitte überprüfen Sie Ihre E-Mails zur Bestätigung."
      );
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Registrierung fehlgeschlagen:", err);
      setError(err.message || "Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      registrationPending.current = false;
      setIsRegistering(false);
      setShowTestNotice(false);
    }
  };

  return (
    <div
      className="sdx greenone-sign-up"
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Hintergrundbild mit Blur */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url('/images/Garage_2.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(8px) brightness(0.55)",
          transform: "scale(1.05)",
        }}
      ></div>

      {/* Registrierungs-Box */}
      <div
        style={{
          position: "relative",
          background: "rgba(255, 255, 255, 0.97)",
          borderRadius: "16px",
          padding: "2rem",
          width: "100%",
          maxWidth: "400px",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
          border: "1px solid #b7cfcc",
          boxSizing: "border-box",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "0.5rem", color: "#004A53" }}>
          Konto erstellen
        </h2>
        <p style={{ textAlign: "center", marginBottom: "1.5rem", color: "#64748b" }}>
          Registrieren Sie Ihr GreenOne-Konto.
        </p>

        <form onSubmit={handleRegister} style={{ display: "grid", gap: "1rem" }}>
          <sdx-input
            label="Firma"
            placeholder="Geben Sie Ihre Firma ein"
            value={company}
            onInput={(e) => setCompany(e.target.value)}
            required
          ></sdx-input>

          <sdx-input
            label="E-Mail"
            type="email"
            placeholder="Geben Sie Ihre E-Mail ein"
            value={email}
            onInput={(e) => setEmail(e.target.value)}
            clarity-mask="True"
            required
          ></sdx-input>

          <div style={{ position: "relative" }}>
            <sdx-input
              label="Passwort"
              ref={passwordRef}
              type="password"
              placeholder="Wählen Sie ein Passwort"
              value={password}
              onInput={(e) => setPassword(e.target.value)}
              clarity-mask="True"
              required
            ></sdx-input>

            {password && (
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                style={{
                  position: "absolute",
                  right: "0.8rem",
                  top: "2.6rem",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  color: "#64748b",
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            )}
          </div>
          <sdx-expand-and-collapse
            expand-label="Passwortkriterien"
            collapse-label="Passwortkriterien">
            <PasswordRequirements password={password} />
          </sdx-expand-and-collapse>

          {/* AGB Bestätigung */}
          <label style={{ fontSize: "0.75rem", color: "#333" }}>
            <input
              type="checkbox"
              checked={acceptedAgb}
              onChange={(e) => setAcceptedAgb(e.target.checked)}
              style={{ marginRight: "0.5rem", accentColor: "#005654" }}
            />
            Ich akzeptiere die{" "}
            <a
              href="https://documents.swisscom.com/product/filestore/lib/3958ad5c-f050-4e19-85c3-204d395067d4/agb_b2b_2021-de.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#026F6D", textDecoration: "underline" }}
            >
              Allgemeinen Geschäftsbedingungen (AGB)
            </a>
          </label>

          {error && <p style={{ color: "red", fontSize: "0.9rem" }}>{error}</p>}
          {message && <p style={{ color: "green", fontSize: "0.9rem" }}>{message}</p>}

          <sdx-button type="submit" variant="primary" label="Registrieren" disabled={isRegistering}></sdx-button>
        </form>

        <div
          style={{
            marginTop: "1.5rem",
            textAlign: "center",
            fontSize: "0.9rem",
          }}
        >
          Sie haben bereits ein Konto?{" "}
          <a
            onClick={(e) => {
              e.preventDefault();
              onBackToLogin();
            }}
            style={{ color: "#026F6D", cursor: "pointer" }}
          >
            Hier anmelden
          </a>
        </div>
      </div>
      {showTestNotice && (
        <TestEnvironmentNotice
          isRegistering={isRegistering}
          onCancel={() => setShowTestNotice(false)}
          onAccept={handleAcceptTestNotice}
        />
      )}
    </div>
  );
}
